/**
 * WVB-02 — Budget Lines Tab (add / edit / delete lines in DRAFT budgets)
 */
import { useState, useCallback } from "react";
import { Plus, Pencil, Trash2, X, Check, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { budgetApi } from "@/services/api";
import { BUDGET_CATEGORIES, UOM_OPTIONS, toMinorUnits, fromMinorUnits, BUDGET_STATUS_CONFIG } from "./EventFinancialsUtils";

const BLANK_FORM = {
  description: "", budget_category: "OTHER", quantity: "1",
  unit_of_measure: "UNIT", unit_rate_display: "",
  contingency_percent: "0", tax_percent: "0", notes: "",
};

function LineForm({ currency, onSave, onCancel, initial = BLANK_FORM }) {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  function handleSave() {
    if (!f.description.trim()) { toast.error("Description is required"); return; }
    if (!f.unit_rate_display) { toast.error("Unit rate is required"); return; }
    const rateMinor = toMinorUnits(f.unit_rate_display, currency);
    if (rateMinor < 0) { toast.error("Unit rate cannot be negative"); return; }
    onSave({
      description: f.description, budget_category: f.budget_category,
      quantity: f.quantity, unit_of_measure: f.unit_of_measure,
      unit_rate_minor: rateMinor, currency_code: currency,
      contingency_percent: f.contingency_percent,
      tax_percent: f.tax_percent,
      notes: f.notes || undefined,
    });
  }

  return (
    <tr className="bg-indigo-50">
      <td className="px-3 py-2">
        <Input value={f.description} onChange={e => set("description", e.target.value)}
          placeholder="Description" className="text-xs h-8" data-testid="line-form-description" />
      </td>
      <td className="px-3 py-2">
        <select value={f.budget_category} onChange={e => set("budget_category", e.target.value)}
          className="w-full border border-slate-200 rounded-md text-xs h-8 px-1.5">
          {BUDGET_CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
      </td>
      <td className="px-3 py-2">
        <Input value={f.quantity} onChange={e => set("quantity", e.target.value)}
          type="number" min="0" step="0.01" className="text-xs h-8 w-20" data-testid="line-form-qty" />
      </td>
      <td className="px-3 py-2">
        <select value={f.unit_of_measure} onChange={e => set("unit_of_measure", e.target.value)}
          className="border border-slate-200 rounded-md text-xs h-8 px-1.5">
          {UOM_OPTIONS.map(u => <option key={u}>{u}</option>)}
        </select>
      </td>
      <td className="px-3 py-2">
        <Input value={f.unit_rate_display} onChange={e => set("unit_rate_display", e.target.value)}
          type="number" min="0" step="0.01" placeholder="0.00" className="text-xs h-8 w-28"
          data-testid="line-form-rate" />
      </td>
      <td className="px-3 py-2">
        <Input value={f.contingency_percent} onChange={e => set("contingency_percent", e.target.value)}
          type="number" min="0" max="100" step="0.01" className="text-xs h-8 w-16" />
      </td>
      <td className="px-3 py-2">
        <Input value={f.tax_percent} onChange={e => set("tax_percent", e.target.value)}
          type="number" min="0" max="100" step="0.01" className="text-xs h-8 w-16" />
      </td>
      <td className="px-3 py-2 text-xs text-slate-400 italic">computed</td>
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <button onClick={handleSave} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" data-testid="line-form-save">
            <Check className="w-4 h-4" />
          </button>
          <button onClick={onCancel} className="p-1.5 text-slate-400 hover:bg-slate-50 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function EventBudgetLinesTab({ eid, budget, linesData, onRefresh }) {
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const bid = budget?.id;
  const isDraft = budget?.status === "DRAFT";
  const lines = linesData?.lines || [];
  const totals = linesData?.totals || {};
  const currency = budget?.currency_code || "INR";
  const statusCfg = BUDGET_STATUS_CONFIG[budget?.status] || {};

  async function handleAddLine(formData) {
    setSaving(true);
    try {
      await budgetApi.addLine(eid, bid, formData);
      toast.success("Line added");
      setAdding(false);
      await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add line");
    } finally { setSaving(false); }
  }

  async function handleUpdateLine(lid, formData) {
    setSaving(true);
    try {
      await budgetApi.updateLine(eid, bid, lid, {
        description: formData.description, budget_category: formData.budget_category,
        quantity: formData.quantity, unit_of_measure: formData.unit_of_measure,
        unit_rate_minor: formData.unit_rate_minor,
        contingency_percent: formData.contingency_percent,
        tax_percent: formData.tax_percent, notes: formData.notes,
      });
      toast.success("Line updated");
      setEditId(null);
      await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update line");
    } finally { setSaving(false); }
  }

  async function handleDeleteLine(lid) {
    if (!window.confirm("Delete this budget line?")) return;
    try {
      await budgetApi.deleteLine(eid, bid, lid);
      toast.success("Line deleted");
      await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete line");
    }
  }

  if (!budget) {
    return (
      <div className="flex flex-col items-center py-16 gap-4 text-slate-500">
        <AlertTriangle className="w-10 h-10 text-amber-300" />
        <p>No budget found. Create a budget in the Overview tab.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="budget-lines-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">{budget.budget_number} v{budget.budget_version}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${statusCfg.cls}`}>{statusCfg.label}</span>
          <span className="text-xs text-slate-400">({lines.length} lines · {currency})</span>
        </div>
        {isDraft && (
          <Button size="sm" onClick={() => setAdding(true)} disabled={adding} data-testid="add-line-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Line
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm" data-testid="budget-lines-table">
          <thead className="bg-slate-50 text-xs text-slate-500">
            <tr>
              <th className="px-3 py-2.5 text-left font-semibold">Description</th>
              <th className="px-3 py-2.5 text-left font-semibold">Category</th>
              <th className="px-3 py-2.5 text-right font-semibold">Qty</th>
              <th className="px-3 py-2.5 text-left font-semibold">UoM</th>
              <th className="px-3 py-2.5 text-right font-semibold">Unit Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold">Cont. %</th>
              <th className="px-3 py-2.5 text-right font-semibold">Tax %</th>
              <th className="px-3 py-2.5 text-right font-semibold">Total</th>
              {isDraft && <th className="px-3 py-2.5" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {adding && <LineForm currency={currency} onSave={handleAddLine} onCancel={() => setAdding(false)} />}
            {lines.length === 0 && !adding && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-slate-400">
                No budget lines yet. {isDraft ? "Click Add Line to start." : ""}
              </td></tr>
            )}
            {lines.map(line => (
              editId === line.id ? (
                <LineForm key={line.id} currency={currency}
                  initial={{ description: line.description, budget_category: line.budget_category,
                    quantity: line.quantity, unit_of_measure: line.unit_of_measure,
                    unit_rate_display: fromMinorUnits(line.unit_rate_minor, currency),
                    contingency_percent: line.contingency_percent, tax_percent: line.tax_percent, notes: line.notes || "" }}
                  onSave={(d) => handleUpdateLine(line.id, d)} onCancel={() => setEditId(null)} />
              ) : (
                <tr key={line.id} className="hover:bg-slate-50 transition-colors" data-testid={`budget-line-${line.id}`}>
                  <td className="px-3 py-2.5 text-slate-800">{line.description}</td>
                  <td className="px-3 py-2.5"><span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{line.budget_category}</span></td>
                  <td className="px-3 py-2.5 text-right text-slate-600">{line.quantity}</td>
                  <td className="px-3 py-2.5 text-slate-400 text-xs">{line.unit_of_measure}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 font-medium">{line.unit_rate_display}</td>
                  <td className="px-3 py-2.5 text-right text-slate-500">{line.contingency_percent}%</td>
                  <td className="px-3 py-2.5 text-right text-slate-500">{line.tax_percent}%</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-800">{line.total_line_display}</td>
                  {isDraft && (
                    <td className="px-3 py-2.5">
                      <div className="flex gap-1">
                        <button onClick={() => setEditId(line.id)} className="p-1 text-slate-400 hover:text-indigo-600 rounded" data-testid={`edit-line-${line.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleDeleteLine(line.id)} className="p-1 text-slate-400 hover:text-red-500 rounded" data-testid={`delete-line-${line.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  )}
                </tr>
              )
            ))}
          </tbody>
          {lines.length > 0 && (
            <tfoot className="bg-slate-50 text-xs font-semibold text-slate-700">
              <tr>
                <td colSpan={7} className="px-3 py-2.5 text-right text-slate-500">Total Forecast Cost</td>
                <td className="px-3 py-2.5 text-right text-indigo-700 text-sm">{totals.total_forecast_cost_display}</td>
                {isDraft && <td />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {lines.length > 0 && (
        <div className="flex flex-wrap gap-4 p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs">
          <div><span className="text-slate-400">Direct Cost: </span><span className="font-semibold text-slate-700">{totals.total_direct_cost_display}</span></div>
          <div><span className="text-slate-400">Contingency: </span><span className="font-semibold text-amber-600">{totals.total_contingency_display}</span></div>
          <div><span className="text-slate-400">Tax: </span><span className="font-semibold text-slate-600">{totals.total_tax_display}</span></div>
          <div><span className="text-slate-400">Forecast Total: </span><span className="font-bold text-indigo-700">{totals.total_forecast_cost_display}</span></div>
        </div>
      )}
    </div>
  );
}
